import React, { useMemo, useState } from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  getPortServiceCategoryLabel,
  NOTIFY_LIST,
  STATUS_KEYS,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NotifyDropdown from "../components/NotifyDropdown";
import type { CancelDrawerInfo } from "../hoc/withDrawers";
import { useCancelPortService } from "../hooks/useCancelPortService";
import { usePortServicesFlow } from "./context/PortServicesFlowContext";
import ATCField from "./extra-fields/ATCField";
import { createAtcSchema } from "./schemas/sharedClosePortServiceSchemas";
import { getAtcAfterAtsError } from "./utils";

const LANG_BASE = "vessel-call.port-services";

interface CancelServiceDrawerProps {
  drawerInfo: CancelDrawerInfo | null;
  onClose: () => void;
  setShouldRefetch: (value: boolean) => void;
}
const TEXTAREA_MAX_CHARS = 1500;

const CancelServiceDrawerDrawer: React.FC<CancelServiceDrawerProps> = ({
  drawerInfo,
  onClose,
  setShouldRefetch,
}) => {
  const { t } = useTranslation();
  const cancelSchema = useMemo(() => {
    const schema = z.object({
      reason: z.string().min(
        1,
        t("form-validation.required", {
          label: t(`${LANG_BASE}.cancel-service-reason`),
        }),
      ),
    });

    if (drawerInfo?.currentStatus === STATUS_KEYS.STARTED) {
      return schema.merge(createAtcSchema()).superRefine((value, ctx) => {
        if (drawerInfo?.currentStatus !== STATUS_KEYS.STARTED) return;

        const error = getAtcAfterAtsError(value.atc, drawerInfo?.ats);

        if (!error) {
          return;
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: error,
        });
      });
    }

    return schema;
  }, [t, drawerInfo?.ats, drawerInfo?.currentStatus]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [notifyOption, setNotifyOption] = useState<string[]>(["Shipping Agent"]);
  const [errors, setErrors] = useState<{
    reason?: string;
    atc?: string;
    notifyOption?: string;
  }>({});
  const { atc, errors: portServiceErrors, setErrors: setPortServiceErrors } = usePortServicesFlow();

  const { onSubmit } = useCancelPortService({
    cancellationReason: reason,
    notifyList: notifyOption,
    portServiceId: drawerInfo?.id || "",
    atc: atc ?? "",
    setShouldRefetch: setShouldRefetch,
  });

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const cancelDialogProps = {
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    content: t(`${LANG_BASE}.cancel-dialog-message`),
    confirmButtonText: t(`${LANG_BASE}.cancel-dialog-confirm-button`),
    cancelButtonText: t("common.close"),
    isInsideDrawer: true,
    handleCloseDialog: () => {
      setCancelDialogOpen(false);
    },
    confirmAction: () => {
      onSubmit();
      setCancelDialogOpen(false);
      onClose();
    },
  };

  const handleSubmit = async () => {
    const result = cancelSchema.safeParse({
      reason: reason,
      atc: atc,
    });

    const fieldErrors: typeof errors = {};

    if (!result.success) {
      for (const issue of result.error.issues) {
        const pathKey = issue.path[0] as keyof typeof fieldErrors;
        if (!pathKey) continue;
        fieldErrors[pathKey] = issue.message;
      }
    }

    if (!notifyOption.includes("Shipping Agent")) {
      fieldErrors.notifyOption = t(`${LANG_BASE}.fields.missing-notify-roles`, {
        roles: "Shipping Agent",
      });
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setPortServiceErrors({ atc: fieldErrors.atc });
      return;
    }

    if (portServiceErrors?.atc) {
      return;
    }
    // clear previous errors and call original submit
    setErrors({});
    setCancelDialogOpen(true);
  };

  if (!drawerInfo) return null;

  return (
    <div data-testid="cancel-port-service-form">
      <SideDrawer
        isOpen={!!drawerInfo?.isOpen}
        title={t(`${LANG_BASE}.cancel-title`)}
        cancelLabel={t("common.close")}
        onClose={onClose}
        submitLabel={t(`${LANG_BASE}.cancel-button`)}
        onSubmit={handleSubmit}
        customFooter={
          <div className="flex flex-col gap-2 px-6 py-6">
            <div className="flex gap-3">
              <Button
                fullWidth
                label={<span className="normal-case font-medium">{t("common.close")}</span>}
                variant="text"
                onClick={onClose}
              />
              <Button
                fullWidth
                label={
                  <span className="normal-case font-medium">{t(`${LANG_BASE}.cancel-button`)}</span>
                }
                variant="contained"
                onClick={handleSubmit}
              />
            </div>
          </div>
        }
      >
        <div className="px-6">
          <div className="min-w-sm max-w-sm">
            <Typography
              variant="body1"
              data-testid="port-service-form-title"
              sx={{ color: "text.primary", pb: 4 }}
            >
              {`${getPortServiceCategoryLabel(drawerInfo?.serviceCategory)}${drawerInfo?.serviceType ? ` - ${drawerInfo.serviceType}` : ""}`}
            </Typography>
            <div className="flex flex-col gap-2">
              <CustomFormLabel data-testid="cancel-service-reason-label">
                {t(`${LANG_BASE}.cancel-service-reason`)}
              </CustomFormLabel>

              <TextField
                multiline
                minRows={4}
                value={reason}
                slotProps={{ htmlInput: { maxLength: TEXTAREA_MAX_CHARS } }}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;

                  if (reason.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      reason: t(`form-validation.max-length`, {
                        label: t(`${LANG_BASE}.cancel-service-reason`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = cancelSchema.safeParse({ reason });

                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, reason: flat.reason?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, reason: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;

                  setReason(val);

                  const res = cancelSchema.safeParse({ reason: val });

                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, reason: flat.reason?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, reason: undefined }));
                  }
                }}
              />
              <div className="flex w-full items-center justify-between gap-3">
                <div className="flex-1">
                  {errors.reason && (
                    <FormHelperText sx={{ color: "error.main", m: 0 }}>
                      {errors.reason}
                    </FormHelperText>
                  )}
                </div>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                >
                  {reason.length}/{TEXTAREA_MAX_CHARS}
                </Typography>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <CustomFormLabel data-testid="cancel-service-notify-label">
                {t(`${LANG_BASE}.notify`)}
              </CustomFormLabel>
              <NotifyDropdown
                options={NOTIFY_LIST}
                option={notifyOption}
                setOption={(opt) => {
                  setNotifyOption(opt);
                  setErrors((prev) => ({ ...prev, notifyOption: undefined }));
                }}
              />
              {errors.notifyOption && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.notifyOption}
                </FormHelperText>
              )}
            </div>
            {drawerInfo.currentStatus === STATUS_KEYS.STARTED && (
              <div className="mt-4">
                <ATCField
                  serviceDetails={{
                    atc: drawerInfo.atc,
                    ats: drawerInfo.ats,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </SideDrawer>
      <GenericDialog open={cancelDialogOpen} {...cancelDialogProps} />
    </div>
  );
};

export default CancelServiceDrawerDrawer;
